import * as Icons from "@nova/component-icons"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { Coordinate, UserLocationShare, VideoColorFinderFlight, VideoFlight } from "@nova/protobuf"
import { VideoMetadata } from "@nova/slice-video"
import classNames from "classnames"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { RootState } from "integrations/redux"
import { useAppSelector } from "integrations/redux/hooks"
import Image from "next/image"
import { RefObject, useCallback, useEffect, useState } from "react"
import { useLazyLoadedImage } from "@nova/toolkit-pure-hooks"
import {
    selectInfraredFlightById,
    selectOrthoFlightById,
} from "features/entity-caching/flights/selectors"

export function GenericBlobUrlAssetsThumbnail({
    blobUrl,
    isFetching,
    isError,
    containerRef,
    display = "contain",
    pad = true,
}: {
    blobUrl: string
    isFetching: boolean
    isError: boolean
    containerRef: RefObject<HTMLDivElement | null>
    display?: "cover" | "contain"
    pad?: boolean
}) {
    const [errored, setErrored] = useState(false)
    useEffect(() => {
        if (isFetching && errored) {
            setErrored(false)
        }
    }, [errored, isFetching])
    if ((!!blobUrl && errored) || isError) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <Icons.Icon Icon={Icons.BrokenImage} iconClassName="text-white-300" />
            </div>
        )
    }
    return (
        <div ref={containerRef} className="w-full h-full">
            {!!blobUrl && !isFetching && !isError && (
                <Image
                    src={blobUrl}
                    alt=""
                    fill
                    className={classNames({
                        "object-contain": display === "contain",
                        "object-cover": display === "cover",
                        "p-1": pad,
                    })}
                    priority
                    unoptimized
                    onError={() => setErrored(true)}
                />
            )}
            {(isFetching || !blobUrl) && !isError && (
                <div className="w-full h-full flex justify-center items-center">
                    <NovaSpinner className="w-4 h-4" />
                </div>
            )}
        </div>
    )
}

const selectCogId = (id: string) => (s: RootState) =>
    selectInfraredFlightById(id)(s)?.cogOrtho?.fileId ??
    selectOrthoFlightById(id)(s)?.cogOrtho?.fileId

export function GenericFlightAssetsThumbnail({ id }: { id: string }) {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const fireId = useAppSelector(selectActivatedFireId)
    const cogId = useAppSelector(selectCogId(id))

    const fetchBlob = useCallback(() => {
        if (!orgId || !fireId) throw new Error("Insufficient context to fetch preview")
        if (cogId) {
            return fetchApiGateway(
                "get /org/:orgId/project/:projectId/flight/:flightId/cog-preview",
                {
                    params: {
                        orgId: orgId,
                        projectId: fireId,
                        flightId: id,
                    },
                    query: `cogId=${cogId}`,
                    headers: { Accept: "image/png" },
                    rawResponse: true, // prevent body from being read before blob()
                }
            ).then((res) => (res as Response).blob())
        } else {
            return fetchApiGateway("get /org/:orgId/project/:projectId/flight/:flightId/preview", {
                params: {
                    orgId: orgId,
                    projectId: fireId,
                    flightId: id,
                },
                headers: { Accept: "image/png" },
                rawResponse: true, // prevent body from being read before blob()
            }).then((res) => (res as Response).blob())
        }
    }, [orgId, fireId, id, cogId])

    const { blobUrl, isFetching, containerRef, isError } = useLazyLoadedImage({
        type: "AssetsThumbnail",
        imageKey: id,
        fetchBlob,
    })
    return (
        <GenericBlobUrlAssetsThumbnail
            blobUrl={blobUrl}
            isFetching={isFetching}
            isError={isError}
            containerRef={containerRef}
        />
    )
}

export function getSVGFromCoordinates({
    points,
    strokeColor = "#E1364B",
    strokeWidth = 1,
    svgWidth = 48,
    svgHeight = 64,
    padding = 4,
    isPolygon,
}: {
    points: Coordinate[]
    strokeColor?: string
    strokeWidth?: number
    svgWidth?: number
    svgHeight?: number
    padding?: number
    isPolygon?: boolean
}) {
    const lats = points.map((p) => p.latitude)
    const lons = points.map((p) => p.longitude)

    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLon = Math.min(...lons)
    const maxLon = Math.max(...lons)

    const latCenter = (minLat + maxLat) / 2
    const lonScaleFactor = Math.cos((latCenter * Math.PI) / 180) // accounts for non-linear longitudes

    const latRange = maxLat - minLat
    const lonRange = (maxLon - minLon) * lonScaleFactor

    const scaleX = (svgWidth - padding * 2) / lonRange
    const scaleY = (svgHeight - padding * 2) / latRange
    const scale = Math.min(scaleX, scaleY)

    const xOffset = (svgWidth - lonRange * scale) / 2
    const yOffset = (svgHeight - latRange * scale) / 2

    const svgPoints = points.map((p) => {
        const adjLon = (p.longitude - minLon) * lonScaleFactor
        const x = adjLon * scale + xOffset
        const y = (maxLat - p.latitude) * scale + yOffset // flip Y axis
        return `${x.toFixed(2)},${y.toFixed(2)}`
    })

    const svgPath = svgPoints.join(" ")
    return (
        <svg width={svgWidth} height={svgHeight}>
            {isPolygon ? (
                <polygon
                    points={svgPath}
                    fill="transparent"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                />
            ) : (
                <polyline
                    points={svgPath}
                    fill="transparent"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                />
            )}
        </svg>
    )
}

export function GenericFlightPathAssetsThumbnail<
    T extends VideoFlight | VideoColorFinderFlight | UserLocationShare,
>({
    id,
    selectEntity,
    getMetadata,
    isLoading = false,
}: {
    id: string
    selectEntity: (id: string) => (s: RootState) => T
    getMetadata: (entity: T | undefined, demoId?: string) => Promise<VideoMetadata | undefined>
    isLoading?: boolean
}) {
    const [hasFetched, setHasFetched] = useState(false)
    const [metadata, setMetadata] = useState<VideoMetadata>()
    const entity = useAppSelector(selectEntity(id))
    const job = "job" in entity ? entity.job : undefined
    useEffect(() => {
        if (!isLoading) {
            ;(async () => {
                try {
                    const metadata = await getMetadata(entity, entity.demoId)
                    setMetadata(metadata)
                } finally {
                    setHasFetched(true)
                }
            })()
        }
    }, [entity, entity.demoId, job, getMetadata, isLoading])
    return (
        <div className="w-full h-full flex justify-center items-center">
            {hasFetched ? (
                <>
                    {!!metadata ? (
                        getSVGFromCoordinates({
                            points: metadata.map(([longitude, latitude]) => ({
                                longitude,
                                latitude,
                            })),
                            strokeColor: "#1E6DA9",
                            strokeWidth: 2,
                            isPolygon: false,
                        })
                    ) : (
                        <Icons.SmallIcon Icon={Icons.BrokenImage} iconClassName="text-white-300" />
                    )}
                </>
            ) : (
                <NovaSpinner className="w-4 h-4" />
            )}
        </div>
    )
}
